import { CloseOutlined } from "@mui/icons-material";
import { Box, DialogContent, Drawer, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
} from "react";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
} from "../../../../../models/user/DynamicForm/DynamicForm";
import {
  GetBOPPropertyDetailsFormJson,
  UpdateBOPPropertyDetails,
} from "../../../../../store/user/policyChange/BOPPolicyChangeActions";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import { GetPolicyRisks, GetPolicyDetailsHeader } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { IBOPPropertyDetailsUpdateRequestParams } from "../../../../../models/user/PolicyChange/BOPPolicyChange";
import { setBOPPropertyDetailsFormFieldsJson } from "../../../../../store/user/policyChange/BOPPolicyChangeSlice";
import { getCookie, formatDateToYYYYMMDD } from "../../../../../utils/Utility";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
  onClose?: () => void; // Add this prop to close the main drawer
}

const UpdatePropertyDetails: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  let { policyId, lobCode } = useParams();

  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);

  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
      dispatch(GetPolicyDetailsHeader({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const { t } = useTranslation();

  const { bopPropertyDetailsFormFieldsJson } = useAppSelector(
    (state: RootState) => state.bopPolicyChange
  );
  
  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      // Add any async functions if needed for BOP property details
    }),
    []
  );

  const handleClose = useCallback(() => {
    dispatch(setBOPPropertyDetailsFormFieldsJson(null));
    setOpen(false);
  }, [setOpen, dispatch]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
    if (onClose) {
      onClose();
    } else {
      handleClose();
    }
  }, [dispatch, handleClose, onClose]);

  const handleUpdatePropertyDetailsFormOnSubmit = (formValues: {
    [key: string]: any;
  }) => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader?.carrierPolicyNumber &&
      userDetails &&
      userDetails.customerId &&
      policyId &&
      system &&
      carrier &&
      lobCode
    ) {
      const changeDate = formValues.effectiveDate
        ? formatDateToYYYYMMDD(new Date(formValues.effectiveDate))
        : formatDateToYYYYMMDD(new Date());
      
      const requestParams: IBOPPropertyDetailsUpdateRequestParams = {
        carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
        changeDt: changeDate,
        customerId: userDetails?.customerId,
        policyRef: policyId,
        carrier: carrier,
        system: system,
        lobCode: lobCode,
        detailDescription: "Updating BOP property details from UI",
        propertyDetails: {
          constructionType: formValues.constructionType,
          roofType: formValues.roofType,
          squareFootage: Number(formValues.squareFootage),
          numberOfStories: Number(formValues.numberOfStories),
        },
      };

      dispatch(UpdateBOPPropertyDetails(requestParams));
    }
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "bopPolicyChange/UpdateBOPPropertyDetails"
    ) {
      setMessageDialogOpen(true);
      dispatch(setCalledReducerType(null));
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(GetBOPPropertyDetailsFormJson());
      dispatch(setCalledReducerType(null));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (bopPropertyDetailsFormFieldsJson) {
      setFormFieldsJson(bopPropertyDetailsFormFieldsJson);
    }
  }, [bopPropertyDetailsFormFieldsJson]);

  return (
    <>
      <MessageDialog
        handleClose={handleMessageDialogClose}
        open={messageDialogOpen}
      />
      <DialogContent
        id="update_property_details_form_content"
        data-test-id="update_property_details_form_content"
        aria-label="Update property details form content"
      >
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleUpdatePropertyDetailsFormOnSubmit}
            asyncOptionsFunctions={asyncFunctions}
          />
        ) : (
          <Box sx={{ padding: "2rem", textAlign: "center" }}>
            <Typography>Loading form...</Typography>
          </Box>
        )}
      </DialogContent>
    </>
  );
};

export default UpdatePropertyDetails; 